import React from 'react';

import { locale } from '@trqk/utils';
import { GradientDot, IconUpArrow, IconDownArrow } from '@trqk/components';

import { CardWrapper } from '../..';
import { mainIconWidth, mainGradientDotWidth } from '../../../constants';
import { DeltaDisplay, WorkInfo, ProDateRange } from './components';

/**
 * @typedef {BumpFalloffCardProps}
 * @param {InsightBumpFalloffReport} bumpFalloffReport the report to render
 */
interface BumpFalloffCardProps {
    bumpFalloffReport: InsightBumpFalloffReport;
    pro: Maybe<string>;
}

/**
 * Shows the details of the Bump and Falloff report: the greatest increase and decrease of a given
 * track between the two most recent consecutive statements.
 * @param {BumpFalloffCardProps} props
 */
export const BumpFalloffCard = ({
    bumpFalloffReport,
    pro,
}: BumpFalloffCardProps): JSX.Element => {
    const totalEarningsDelta = bumpFalloffReport.total_earnings_delta;
    const totalEarningsPercentDelta =
        bumpFalloffReport.total_earnings_percent_delta;
    const isIncrease =
        totalEarningsDelta !== null &&
        totalEarningsDelta !== undefined &&
        totalEarningsDelta > 0;

    const bigArrow = isIncrease ? (
        <IconUpArrow width={mainIconWidth} fillColor="secondary-500" />
    ) : (
        <IconDownArrow width={mainIconWidth} fillColor="quinary-500" />
    );

    const dotGradientColor = isIncrease ? 'secondary' : 'quinary';
    const verb = isIncrease
        ? locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF.INCREASE
        : locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF.DECREASE;

    return (
        <CardWrapper>
            <div className="flex flex-col h-full">
                <div className="flex-none flex flex-col items-center justify-center mt-8">
                    <GradientDot
                        width={mainGradientDotWidth}
                        gradientColor={dotGradientColor}
                    >
                        {bigArrow}
                    </GradientDot>
                    <h4 className="mt-4 mb-2">
                        {locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF.TITLE}
                    </h4>
                    <div className="mx-4 text-center">
                        {locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF.HEADER.PART_1}
                        <br />
                        <ProDateRange
                            bumpFalloffReport={bumpFalloffReport}
                            pro={pro}
                        />
                    </div>
                    {totalEarningsDelta !== null && (
                        <>
                            <div className="mt-4 text-sm">
                                {verb}&nbsp;
                                {locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF.OF}
                            </div>
                            <DeltaDisplay
                                displayMode="flex"
                                earningsDelta={totalEarningsDelta ?? 0}
                                earningsPercentDelta={
                                    totalEarningsPercentDelta ?? 0
                                }
                                size="lg"
                            />
                        </>
                    )}
                </div>
                <div className="flex-grow px-8 mb-6 grid grid-rows-2">
                    {bumpFalloffReport.bump !== null && (
                        <WorkInfo
                            label={
                                locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF
                                    .TOP_INCREASE
                            }
                            trackTitle={bumpFalloffReport?.bump?.title ?? null}
                            earningsDelta={
                                bumpFalloffReport?.bump?.earnings_delta ?? null
                            }
                            earningsPercentDelta={
                                bumpFalloffReport?.bump
                                    ?.earnings_percent_delta ?? null
                            }
                        />
                    )}
                    {bumpFalloffReport.falloff !== null && (
                        <WorkInfo
                            label={
                                locale.enUS.INSIGHTS.CARDS.BUMP_FALLOFF
                                    .TOP_DECREASE
                            }
                            trackTitle={
                                bumpFalloffReport?.falloff?.title ?? null
                            }
                            earningsDelta={
                                bumpFalloffReport?.falloff?.earnings_delta ??
                                null
                            }
                            earningsPercentDelta={
                                bumpFalloffReport?.falloff
                                    ?.earnings_percent_delta ?? null
                            }
                        />
                    )}
                </div>
            </div>
        </CardWrapper>
    );
};
